import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Main as Container } from 'Components/Main';
import { getUsers, deleteUser } from 'services/users-service';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { Dialog, PageHeader, Select, TextField, Button, EditSvg } from 'Components/ui';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

// ─── Avatar helpers ───────────────────────────────────────────────────────────

const AVATAR_GRADIENTS = [
  'var(--vo-avatar-grad-1)',
  'var(--vo-avatar-grad-2)',
  'var(--vo-avatar-grad-3)',
  'var(--vo-avatar-grad-4)',
  'var(--vo-avatar-grad-5)',
  'var(--vo-avatar-grad-6)',
  'var(--vo-avatar-grad-7)',
  'var(--vo-avatar-grad-8)',
];

const getGradient = name =>
  AVATAR_GRADIENTS[(name || ' ').charCodeAt(0) % AVATAR_GRADIENTS.length];

const getInitials = name => {
  const parts = (name || '?').trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : (name || '?').substring(0, 2).toUpperCase();
};

// ─── Shimmer animation ────────────────────────────────────────────────────────

const shimmerMove = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: calc(400px + 100%) 0; }
`;

const ShimmerBlock = styled.div`
  height: ${({ $h }) => $h || '14px'};
  width: ${({ $w }) => $w || '100%'};
  border-radius: 6px;
  background: linear-gradient(90deg, var(--vo-mist) 25%, var(--vo-line) 50%, var(--vo-mist) 75%);
  background-size: 400px 100%;
  animation: ${shimmerMove} 1.2s infinite linear;
`;

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;

// Info banner
const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: white;
  border-radius: 14px;
  border-left: 4px solid var(--color-brand);
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;

const InfoIcon = styled.div`
  flex-shrink: 0;
  color: var(--color-brand);
  margin-top: 1px;
`;

const InfoText = styled.div``;

const InfoTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`;

const InfoDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
  line-height: 1.55;
`;

// Filter card
const FilterCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FilterLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-slate);
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ClearBtn = styled.button`
  height: 40px;
  padding: 0 20px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-slate);
  background: white;
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: var(--vo-mist); }
`;

const FilterBtn = styled.button`
  height: 40px;
  padding: 0 20px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background: var(--color-brand);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;

  &:hover { background: var(--color-brand-strong); }
`;

// Table
const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
  margin-bottom: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: var(--vo-mist);
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid var(--vo-cloud);
  white-space: nowrap;
`;

const Tr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`;

const Td = styled.td`
  padding: 12px 16px;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarInitials = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 9px;
  font-weight: 700;
  color: white;
  line-height: 1;
`;

const UserName = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--vo-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmailText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`;

const PermissionText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
`;

const StatusBadge = styled.span`
  display: inline-block;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  color: ${({ $active }) => ($active ? 'var(--vo-success)' : 'var(--vo-slate)')};
  background: ${({ $active }) => ($active ? 'var(--vo-success-10)' : 'var(--vo-slate-10)')};
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;


const EmptyRow = styled.td`
  padding: 40px 16px;
  text-align: center;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-steel);
`;

// Pagination
const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 0;
`;

const PageBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: ${({ $active }) => ($active ? 'none' : '1px solid var(--vo-line)')};
  background: ${({ $active }) => ($active ? 'var(--color-brand)' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--vo-ink-2)')};
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? '700' : '400')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${({ $active }) => ($active ? 'var(--color-brand-strong)' : 'var(--vo-mist)')};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Ellipsis = styled.span`
  width: 32px;
  text-align: center;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`;

// ─── Pagination component ─────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;

  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, start + 4);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  return (
    <PaginationRow>
      <PageBtn onClick={() => onChange(page - 1)} disabled={page === 1}>‹</PageBtn>

      {start > 1 && (
        <>
          <PageBtn onClick={() => onChange(1)}>1</PageBtn>
          {start > 2 && <Ellipsis>…</Ellipsis>}
        </>
      )}

      {pages.map(p => (
        <PageBtn key={p} $active={p === page} onClick={() => onChange(p)}>{p}</PageBtn>
      ))}

      {end < totalPages && (
        <>
          {end < totalPages - 1 && <Ellipsis>…</Ellipsis>}
          <PageBtn onClick={() => onChange(totalPages)}>{totalPages}</PageBtn>
        </>
      )}

      <PageBtn onClick={() => onChange(page + 1)} disabled={page === totalPages}>›</PageBtn>
    </PaginationRow>
  );
};

// ─── Shimmer rows ─────────────────────────────────────────────────────────────

const ShimmerRow = () => (
  <Tr>
    <Td>
      <NameCell>
        <ShimmerBlock $w="26px" $h="26px" style={{ borderRadius: '7px', flexShrink: 0 }} />
        <ShimmerBlock $w="120px" $h="14px" />
      </NameCell>
    </Td>
    <Td><ShimmerBlock $w="150px" $h="13px" /></Td>
    <Td><ShimmerBlock $w="80px" $h="13px" /></Td>
    <Td><ShimmerBlock $w="50px" $h="20px" style={{ borderRadius: '20px' }} /></Td>
    <Td>
      <ActionsCell>
        <ShimmerBlock $w="30px" $h="30px" style={{ borderRadius: '8px' }} />
        <ShimmerBlock $w="30px" $h="30px" style={{ borderRadius: '8px' }} />
        <ShimmerBlock $w="30px" $h="30px" style={{ borderRadius: '8px' }} />
      </ActionsCell>
    </Td>
  </Tr>
);

// ─── Component ────────────────────────────────────────────────────────────────

export const Users = () => {
  const { t } = useTranslation(['user', 'common']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);

  // Filter inputs (live)
  const [nameInput, setNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [permissionInput, setPermissionInput] = useState('');
  const [statusInput, setStatusInput] = useState('');

  // Applied filters (trigger query)
  const [applied, setApplied] = useState({ name: '', email: '', permission: '', status: '' });
  const [page, setPage] = useState(1);

  const buildParams = () => {
    const { name, email, permission, status } = applied;
    let typeFilter = '';
    let searchContent = '';
    if (permission && permission !== 'all') { typeFilter = 'Permissão'; searchContent = permission; }
    else if (email)  { typeFilter = 'Email';     searchContent = email; }
    else if (name)   { typeFilter = 'Nome';      searchContent = name; }
    const params = { typeFilter, searchContent };
    if (status === 'true') params.active = true;
    else if (status === 'false') params.active = false;
    return params;
  };

  const { isLoading, data: pagedUsers } = useQuery(
    ['getUsers', applied, page],
    async () => {
      const { typeFilter, searchContent, active } = buildParams();
      const result = await getUsers(selectedTenant.slug, page, 15, typeFilter, searchContent, active);
      if (result?.data?.data?.length > 0) return result.data;
      return { data: [], page, totalPages: 0 };
    },
    { enabled: !!selectedTenant.slug, keepPreviousData: true }
  );

  const handleFilter = () => {
    setApplied({ name: nameInput, email: emailInput, permission: permissionInput, status: statusInput });
    setPage(1);
  };

  const handleClear = () => {
    setNameInput(''); setEmailInput(''); setPermissionInput(''); setStatusInput('');
    setApplied({ name: '', email: '', permission: '', status: '' });
    setPage(1);
  };

  const handleDeactivate = async user => {
    const confirmed = await Dialog.confirm({
      title: t('user:alert.warningTitle'),
      message: t('user:users.delete.confirm', { name: user.name }),
      confirmColor: 'var(--vo-danger)',
      confirmLabel: t('common:buttons.confirm') || 'Confirmar',
      cancelLabel: t('common:buttons.cancel') || 'Cancelar',
    });
    if (!confirmed) return;
    try {
      const res = await deleteUser(user._id);
      if (res.status === 204) {
        Dialog.alert({ title: t('user:alert.successTitle'), message: t('user:users.success.disabled'), icon: 'success' });
        setApplied(prev => ({ ...prev }));
      } else {
        throw new Error('Erro ao desativar usuário.');
      }
    } catch (err) {
      Dialog.alert({ title: t('user:alert.errorTitle'), message: err.response?.data?.message ?? err.message, icon: 'error' });
    }
  };

  const users = pagedUsers?.data ?? [];
  const totalPages = pagedUsers?.totalPages ?? 0;

  return (
    <Container>
      <PageWrap>
        {/* Header */}
        <PageHeader
          title={t('user:users.title')}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: t('user:users.title') },
          ]}
          actions={
            <Button
              variant="primary"
              onClick={() => navigate('/users/add')}
              startIcon={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              }
              sx={{ height: '44px', padding: '0 20px' }}
            >
              {t('user:users.addNew')}
            </Button>
          }
        />

        {/* Info banner */}
        <InfoBanner>
          <InfoIcon>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </InfoIcon>
          <InfoText>
            <InfoTitle>{t('user:users.infoTitle')}</InfoTitle>
            <InfoDesc>{t('user:users.description')}</InfoDesc>
          </InfoText>
        </InfoBanner>

        {/* Filter card */}
        <FilterCard>
          <FilterGrid>
            <FilterGroup>
              <FilterLabel>{t('user:users.filter.name')}</FilterLabel>
              <TextField
                fullWidth
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleFilter()}
                placeholder={t('user:users.filter.namePlaceholder')}
              />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>{t('user:users.filter.email')}</FilterLabel>
              <TextField
                fullWidth
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleFilter()}
                placeholder={t('user:users.filter.emailPlaceholder')}
              />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>{t('user:users.filter.permission')}</FilterLabel>
              <Select
                name="permissionInput"
                value={permissionInput}
                onChange={setPermissionInput}
                options={[
                  { value: '', label: t('user:users.filter.selectPermission') },
                  { value: 'all', label: t('user:users.filter.permissionAll') },
                  { value: 'Admin', label: t('user:users.filter.permAdmin') },
                  { value: 'Usuário Padrão', label: t('user:users.filter.permStandard') },
                  { value: 'Visualizador', label: t('user:users.filter.permViewer') },
                  { value: 'Auditor', label: t('user:users.filter.permAuditor') },
                  { value: 'DPO', label: t('user:users.filter.permDpo') },
                ]}
              />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>{t('user:users.filter.status')}</FilterLabel>
              <Select
                name="statusInput"
                value={statusInput}
                onChange={setStatusInput}
                options={[
                  { value: '', label: t('user:users.filter.selectStatus') },
                  { value: 'all', label: t('user:users.filter.statusAll') },
                  { value: 'true', label: t('user:users.filter.statusActive') },
                  { value: 'false', label: t('user:users.filter.statusInactive') },
                ]}
              />
            </FilterGroup>
          </FilterGrid>

          <FilterActions>
            <ClearBtn type="button" onClick={handleClear}>
              {t('user:users.filter.clear')}
            </ClearBtn>
            <FilterBtn type="button" onClick={handleFilter}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              {t('user:users.filter.search')}
            </FilterBtn>
          </FilterActions>
        </FilterCard>

        {/* Table */}
        <TableCard>
          <Table>
            <Thead>
              <tr>
                <Th>{t('user:users.table.name')}</Th>
                <Th>{t('user:users.table.email')}</Th>
                <Th>{t('user:users.table.permission')}</Th>
                <Th>{t('user:users.table.status')}</Th>
                <Th $center>{t('user:users.table.actions')}</Th>
              </tr>
            </Thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => <ShimmerRow key={i} />)
              ) : users.length === 0 ? (
                <tr>
                  <EmptyRow colSpan={5}>{t('user:users.noData')}</EmptyRow>
                </tr>
              ) : (
                users.map(user => (
                  <Tr key={user._id}>
                    <Td>
                      <NameCell>
                        <Avatar $gradient={getGradient(user.name)}>
                          <AvatarInitials>{getInitials(user.name)}</AvatarInitials>
                        </Avatar>
                        <UserName>{user.name}</UserName>
                      </NameCell>
                    </Td>
                    <Td><EmailText>{user.email}</EmailText></Td>
                    <Td><PermissionText>{user.permission || user.type || '—'}</PermissionText></Td>
                    <Td>
                      <StatusBadge $active={user.active}>
                        {user.active
                          ? t('user:users.filter.statusActive')
                          : t('user:users.filter.statusInactive')}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <ActionsCell>
                        {/* View */}
                        <Button iconOnly variant="icon" title="Ver" onClick={() => navigate(`/users/show/${user._id}`)}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </Button>
                        {/* Edit */}
                        <Button iconOnly variant="icon" title={t('common:tooltips.edit')} onClick={() => navigate(`/users/edit/${user._id}`)}>
                          <EditSvg />
                        </Button>
                        {/* Deactivate */}
                        <Button iconOnly variant="icon" danger title={t('user:users.table.deactivateAction')} onClick={() => handleDeactivate(user)}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                          </svg>
                        </Button>
                      </ActionsCell>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </Table>
        </TableCard>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} onChange={p => setPage(p)} />
        )}
      </PageWrap>
    </Container>
  );
};
